/**
 * General terminal commands for basic terminal operations and information.
 *
 * @module terminal/commands/general
 */
import type { CommandResult, CommandContext } from './types';

/**
 * General commands available in the terminal.
 * Includes help, about, team info, contact details, and terminal control.
 *
 * @example
 * ```
 * help          - Show available commands
 * about         - Company information
 * team          - Team members
 * contact       - Contact details
 * games         - List available games
 * animations    - List available animations
 * tools         - List available tools
 * clear         - Clear terminal screen
 * exit          - Close terminal
 * ```
 */
export const generalCommands: Record<
	string,
	(args: string[], context: CommandContext) => CommandResult | null
> = {
	help: () => ({
		type: 'text',
		lines: [
			'Available commands:',
			'',
			'  GENERAL',
			'  help        - Show this help message',
			'  about       - Learn about Ameta',
			'  team        - Meet the team',
			'  contact     - Contact information',
			'  clear       - Clear terminal',
			'  exit        - Close terminal',
			'',
			'  SYSTEM',
			'  whoami      - Current user',
			'  pwd         - Working directory',
			'  ls          - List files',
			'  date        - Current date/time',
			'  uptime      - System uptime',
			'  neofetch    - System info',
			'',
			'  FILES (use "cat <filename>")',
			'  cat contact.txt / kontakt.txt',
			'  cat about-us.md / om-oss.md',
			'  cat services / tjenester',
			'  cat projects / prosjekter',
			'',
			'  INFO',
			'  stack/tech  - Tech stack',
			'  integrations - Integrations list',
			'  culture     - Company values',
			'  how/method  - Our methodology',
			'  blog        - Knowledge base',
			'  jobs/careers - Interactive coding challenge',
			'  privacy     - Privacy info',
			'',
			'  GAMES (type "games" for full list)',
			'  snake, tetris, pong, guess',
			'  breakout, tictactoe, typing',
			'',
			'  ANIMATIONS (type "animations" for full list)',
			'  matrix, fireworks, hack, glitch',
			'  rain, particles, typewriter, nyancat, coffee',
			'',
			'  TOOLS (type "tools" for full list)',
			'  pomodoro, timer, json',
			'',
			'  EFFECTS',
			'  spin, flip, barrel roll, crt',
			'',
			'  FUN',
			'  joke, fortune, cowsay, ascii [text]',
			'  weather     - Local weather',
			'',
			'  CUSTOMIZATION',
			'  theme       - Change terminal theme',
			'  themes      - List available themes',
			'  english/norsk - Change language',
			'',
			'  AI CHAT',
			'  ask <msg>  - Ask the AI assistant',
			'  Or just type anything to chat!',
			'',
			'  EASTER EGGS',
			'  Try: iddqd, rickroll, nyan, sl, 42'
		]
	}),

	games: () => ({
		type: 'text',
		lines: [
			'╔═══════════════════════════════════════╗',
			'║           AVAILABLE GAMES             ║',
			'╠═══════════════════════════════════════╣',
			'║  snake      - Classic Snake game      ║',
			'║  tetris     - Play Tetris             ║',
			'║  pong       - Pong vs AI              ║',
			'║  guess      - Guess the number (1-100)║',
			'║  breakout   - Breakout / Arkanoid     ║',
			'║  tictactoe  - Tic Tac Toe vs AI       ║',
			'║  typing     - Typing speed test       ║',
			'╠═══════════════════════════════════════╣',
			'║  Controls: Arrow keys or WASD         ║',
			'║  ESC to exit any game                 ║',
			'╚═══════════════════════════════════════╝'
		]
	}),

	animations: () => ({
		type: 'text',
		lines: [
			'╔═══════════════════════════════════════╗',
			'║         AVAILABLE ANIMATIONS          ║',
			'╠═══════════════════════════════════════╣',
			'║  matrix     - Enter the matrix        ║',
			'║  fireworks  - Watch fireworks          ║',
			'║  hack       - Hack the mainframe      ║',
			'║  glitch     - VHS glitch effect        ║',
			'║  rain       - Watch it rain            ║',
			'║  particles  - Interactive particles    ║',
			'║  typewriter - Typewriter effect        ║',
			'║  nyancat    - Nyan Cat!                ║',
			'║  coffee     - Brew some coffee         ║',
			'╠═══════════════════════════════════════╣',
			'║  ESC to exit any animation             ║',
			'╚═══════════════════════════════════════╝'
		]
	}),

	tools: () => ({
		type: 'text',
		lines: [
			'╔═══════════════════════════════════════╗',
			'║          AVAILABLE TOOLS              ║',
			'╠═══════════════════════════════════════╣',
			'║  pomodoro   - Pomodoro timer           ║',
			'║  timer [t]  - Countdown timer          ║',
			'║  json       - JSON formatter           ║',
			'╠═══════════════════════════════════════╣',
			'║  ESC to exit any tool                  ║',
			'╚═══════════════════════════════════════╝'
		]
	}),

	about: () => ({
		type: 'text',
		lines: [
			'AMETA - Industrial Data and Automation',
			'',
			'We solve the problems others say no to.',
			'',
			'Founded: 2019',
			'Location: Kristiansand, Norway',
			'Focus: Industrial IoT, Predictive Maintenance, Systems Integration',
			'',
			'We take the messy legacy systems, the impossible integrations,',
			'and the equipment that "can\'t be connected" - and we connect it.'
		]
	}),

	team: () => ({
		type: 'text',
		lines: [
			'THE TEAM',
			'',
			'┌─────────────────────────────────────────────────────────┐',
			'│ Eivind Klevmoen Døvik    CEO       Business Analytics   │',
			'│ Erik Trædal              CTO       17y Enterprise Dev   │',
			'│ Stian Berge Jacobsen     Lead      OEE Specialist       │',
			'│ Luke Curtis              Senior    Accessibility Pro    │',
			'│ Halvor Smørvik           Dev       AI/ML Expert         │',
			'│ Adrian Bøe               Dev       Security Focus       │',
			'└─────────────────────────────────────────────────────────┘'
		]
	}),

	contact: () => ({
		type: 'text',
		lines: [
			'CONTACT',
			'',
			'Email:    hello@ameta.no',
			'Address:  Kjøita 40, 4630 Kristiansand, Norway',
			'Web:      https://ameta.no',
			'',
			'We respond within 24 hours.'
		]
	}),

	clear: () => ({ type: 'clear' }),
	exit: () => ({ type: 'exit', lines: ['Goodbye.'] })
};
